import type { EmployeeDebtAlert } from '@cpce/shared';
import { format } from 'date-fns';
import { CircleAlert, RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { useExportEmployeeFinancialExchanges } from '@/api/employees/employees.downloads';
import type { EmployeeWithBeneficiariesFormatted } from '@/api/employees/employees.types';
import { Button } from '@/components/Button';
import { DatePicker } from '@/components/DatePicker';
import { ExportButton } from '@/components/ExportButton';
import { InfoCard } from '@/components/InfoCard';
import { SearchInput } from '@/components/SearchInput';
import { Table } from '@/components/table/Table';
import { useTableState } from '@/components/table/useTableState';
import { useCommiteeSlugNavigate } from '@/utils/router';
import { formatDecimalAsPrice } from '@/utils/string.utils';
import { employeeTransactionsColumns } from '../columns/employee-transactions-columns';
import { DebtRemindersModal } from '../debts/components/DebtRemindersModal';

interface EmployeeFinancialExchangesProps {
  employee: EmployeeWithBeneficiariesFormatted;
}

export function EmployeeFinancialExchanges(props: EmployeeFinancialExchangesProps) {
  const { employee } = props;
  const [dateFilter, setDateFilter] = useState<Date | null>(null);
  const tableState = useTableState('employee-financial-exchanges', {
    columns: employeeTransactionsColumns,
  });
  const { download } = useExportEmployeeFinancialExchanges(employee.id);
  const debt = employee.alerts.debt;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <DatePicker value={dateFilter} onChange={setDateFilter} />
        <SearchInput search={tableState.search} setSearch={tableState.setSearch} />
        <ExportButton onClick={download} />
      </div>
      {debt && <DebtCard debt={debt} employee={employee} />}
      <Table
        entity={{ singular: 'échange financier', plural: 'échanges financiers' }}
        data={employee.transactions}
        columns={employeeTransactionsColumns}
        // The DatePicker owns the date column and stays ephemeral: it is injected over the
        // controller's filters so it is neither persisted nor cleared by the reset strip.
        tableState={{
          ...tableState,
          columnFilters: [
            ...tableState.columnFilters.filter((filter) => filter.id !== 'date'),
            ...(dateFilter ? [{ id: 'date', value: dateFilter }] : []),
          ],
        }}
      />
    </div>
  );
}

interface DebtCardProps {
  debt: EmployeeDebtAlert;
  employee: EmployeeWithBeneficiariesFormatted;
}

function DebtCard(props: DebtCardProps) {
  const { debt, employee } = props;

  const navigate = useCommiteeSlugNavigate();
  const communications = employee.employeeCommunications ?? [];
  const lastPaymentReminder = communications[communications.length - 1];
  const [remindersOpen, setRemindersOpen] = useState(false);

  return (
    <InfoCard
      title="Salarié en dette"
      icon={CircleAlert}
      header={
        <div className="flex grow justify-end text-xl font-bold">
          {formatDecimalAsPrice(debt.total)}
        </div>
      }
      color="red"
    >
      <div className="flex flex-col gap-4">
        <div className="text-gray-700">
          Ce salarié présente un solde débiteur. Montant total de la dette:{' '}
          {formatDecimalAsPrice(debt.total)}
        </div>
        <div className="flex gap-2 justify-between">
          <div className="flex items-center gap-4">
            <Button className="text-lg" icon={RefreshCcw} onClick={() => setRemindersOpen(true)}>
              Relancer
            </Button>
            <DebtRemindersModal
              open={remindersOpen}
              closeModal={() => setRemindersOpen(false)}
              participationIds={debt.prestationParticipationIds}
              employeeCount={1}
              total={debt.total}
            />
            {lastPaymentReminder && (
              <div className="text-gray-700 text-sm">
                Dernière relance envoyée le {format(lastPaymentReminder.dateTime, 'dd/MM/yyyy')}
              </div>
            )}
          </div>
          <Button
            variant="color"
            className="bg-red-600 px-8"
            onClick={() => {
              navigate({
                to: '/$commiteeSlug/beneficiaries/transactions/create',
                search: {
                  employeeId: employee.id,
                  prestationParticipationIds: debt.prestationParticipationIds,
                },
              });
            }}
          >
            Régler
          </Button>
        </div>
      </div>
    </InfoCard>
  );
}
